import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { eventAPI, favoriteAPI, publicAPI } from '@/services/api';
import { ExternalLink } from 'lucide-react';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import ImageViewer from "@/components/ImageViewer";
import VideoViewer from "@/components/VideoViewer";
import BackLink from "@/components/BackLink";
import LocationViewer from "@/components/LocationViewer";
import StickyInfoCard from "@/components/StickyInfoCard";
import { formatEventDate } from "@/utils/dateUtil";

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
	iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
	shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

export default function EventDetailPage() {
	const { id } = useParams();
	const { t } = useTranslation();
	const { user } = useAuth();
	const navigate = useNavigate();
	const [ad, setAd] = useState(null);
	const [loading, setLoading] = useState(true);
	const [isFav, setIsFav] = useState(false);
	const [pricing, setPricing] = useState({
		free_images_limit: 5, price_extra_image: 0.70,
		free_videos_limit: 1, price_extra_video: 3.00,
		max_video_size_mb: 50, price_featured_ad: 10.00,
		vat_percent: 23
	});

	const API_BASE = process.env.REACT_APP_BACKEND_URL;

	useDocumentMeta({
		title: ad ? `${ad.title} | Equi-Market` : 'Equi-Market',
		description: ad?.description?.slice(0, 200),
		url: ad ? `${window.location.origin}/events/${ad.slug || ad.id}` : '',
		price: ad?.price,
		currency: 'EUR',
		type: 'event',
	});

	useEffect(() => {
		setLoading(true);
		publicAPI
			.settings()
			.then(r => setPricing(prev => ({ ...prev, ...r.data })))
			.catch(() => {});
		eventAPI
			.get(id)
			.then((r) => {
				setAd(r.data);
				if (user) {
					favoriteAPI
						.check(r.data.id)
						.then(f => setIsFav(f.data.is_favorite))
						.catch(() => {});
				}
			})
			.catch(() => navigate('/events'))
			.finally(() => setLoading(false));
	}, [id, navigate]);

	if (loading || !ad) return (
		<div className="pt-24 min-h-screen flex items-center justify-center">
			<div className="w-8 h-8 border-2 border-[#1A3C34] border-t-transparent rounded-full animate-spin" />
		</div>
	);

	const hiddenImages = ad.hidden_images || [];
	const hiddenVideos = ad.hidden_videos || [];
	const images = (ad.images || []).filter(url => !hiddenImages.includes(url)).map((url, i) => ({
		url,
		preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
		thumbnail: ad.thumbnails?.[i] ? (ad.thumbnails[i].startsWith('/') ? `${ API_BASE }${ ad.thumbnails[i] }` : ad.thumbnails[i]) : null
	}));
	const videos = (ad.videos || []).filter(url => !hiddenVideos.includes(url)).map(url => ({
		url,
		preview: url.startsWith('/') ? `${ API_BASE }${ url }` : url,
		name: url.split('/').pop()
	}));
	const hasLocation = ad.location_lat && ad.location_lng;

	const disciplines = ad.disciplines || [];
	const registrationDeadline = ad.registration_deadline || null;

	return (
		<div className="pt-20 md:pt-24 min-h-screen bg-[#F9F7F2]" data-testid={`event-detail-${ad.id}`}>
			<div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

				{/* Breadcrumb */}
				<BackLink title={ad.title} uri="/events" textKey='nav.events' />

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

					{/* Left - Images & Content */}
					<div className="lg:col-span-2 space-y-6">

						{/* Main Image */}
						<ImageViewer images={images} title={ad.title} is_featured={ad.is_featured} />

						{ videos.length > 0 &&
							<VideoViewer videos={videos} />
						}

						<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
							<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">
								{t('horse.description')}
							</h2>
							<p className="text-sm text-[#3a3a3a] leading-relaxed whitespace-pre-line">{ad.description}</p>
						</div>

						{disciplines.length > 0 && (
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
								<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">
									{t('event.disciplines')}
								</h2>
								<div className="flex flex-wrap gap-2">
									{disciplines.map(d => (
										<span key={d} className="text-xs bg-[#F2F0E9] text-[#1A3C34] px-3 py-1.5 rounded-sm border
												border-[#E6E2D6]" data-testid={`event-discipline-tag-${d}`}>
                      {t(`trainer.discipline_opt.${d}`, d)}
                    </span>
									))}
								</div>
							</div>
						)}

						{registrationDeadline && (
							<div className="bg-white border border-[#E6E2D6] rounded-sm p-6">
								<h2 className="text-xs font-bold text-[#8B4513] uppercase tracking-[0.2em] mb-3">
									{t('event.registration')}
								</h2>
								<p className="text-sm text-[#3a3a3a]">{t('event.deadline')}:
									<strong>{formatEventDate(registrationDeadline)}</strong>
								</p>
								{ad.registration_url && (
									<a href={ad.registration_url} target="_blank" rel="noreferrer"
										 className="mt-3 inline-flex items-center gap-2 text-sm bg-[#1A3C34] hover:bg-[#153029]
										 		text-white px-4 py-2 rounded-sm" data-testid="event-register-btn">
										<ExternalLink className="w-4 h-4" /> {t('event.register_now')}
									</a>
								)}
							</div>
						)}

						{/* Map */ }
						{ hasLocation &&
							<LocationViewer title={ ad.title }
															location_name={ ad.location_name }
															location_lat={ ad.location_lat }
															location_lng={ ad.location_lng }/>
						}

					</div>

					{/* Right - Sticky Info */}
					<StickyInfoCard ad={ad}
													adType="event"
													is_favorite={isFav}
													pricing={pricing} />

				</div>
			</div>
		</div>
	);
}